import { createStore as createVanillaStore, type StoreApi } from 'zustand/vanilla'

// Find/Replace state for one Grid mount. Kept in its own per-instance
// store rather than the shared grid-store so the dialog can stay open
// across selection changes without leaking churn into every cell
// subscription. Match recomputation is driven by useFindActions; this
// store is purely the dialog's UI + match-list state container.

export type FindMode = 'find' | 'replace'

export type FindScope = 'sheet' | 'workbook'

export interface FindMatch {
    sheetId: string
    row: number
    col: number
}

export interface FindState {
    isOpen: boolean
    mode: FindMode
    query: string
    replacement: string
    matchCase: boolean
    wholeCell: boolean
    useRegex: boolean
    searchInFormulas: boolean
    scope: FindScope
    matches: FindMatch[]
    currentMatchIndex: number
    // Set when useRegex is true and the user's pattern fails to compile.
    // The dialog renders this inline; clearing the query (or fixing the
    // pattern) clears it on the next setMatches call.
    regexError: string | null
}

export interface FindActions {
    open: (mode: FindMode) => void
    close: () => void
    setMode: (mode: FindMode) => void
    setQuery: (q: string) => void
    setReplacement: (r: string) => void
    setMatchCase: (v: boolean) => void
    setWholeCell: (v: boolean) => void
    setUseRegex: (v: boolean) => void
    setSearchInFormulas: (v: boolean) => void
    setScope: (s: FindScope) => void
    setMatches: (matches: FindMatch[], regexError: string | null) => void
    setCurrentMatchIndex: (index: number) => void
}

export type FindStore = FindState & FindActions

export type FindStoreApi = StoreApi<FindStore>

const INITIAL: FindState = {
    isOpen: false,
    mode: 'find',
    query: '',
    replacement: '',
    matchCase: false,
    wholeCell: false,
    useRegex: false,
    searchInFormulas: false,
    scope: 'sheet',
    matches: [],
    currentMatchIndex: -1,
    regexError: null,
}

export function createFindStore(): FindStoreApi {
    return createVanillaStore<FindStore>(set => ({
        ...INITIAL,
        // Re-opening keeps the previous query/options so the user can
        // iterate quickly. The match list is regenerated by
        // useFindActions' input-key subscription as soon as isOpen flips.
        open: mode => set({ isOpen: true, mode }),
        close: () =>
            set({
                isOpen: false,
                matches: [],
                currentMatchIndex: -1,
                regexError: null,
            }),
        setMode: mode => set({ mode }),
        setQuery: query => set({ query }),
        setReplacement: replacement => set({ replacement }),
        setMatchCase: matchCase => set({ matchCase }),
        setWholeCell: wholeCell => set({ wholeCell }),
        setUseRegex: useRegex => set({ useRegex }),
        setSearchInFormulas: searchInFormulas => set({ searchInFormulas }),
        setScope: scope => set({ scope }),
        setMatches: (matches, regexError) =>
            set(s => {
                // When matches change, snap the cursor to the first
                // match if it was previously out-of-range. If the
                // previous current index still refers to a valid match
                // by (sheetId,row,col), keep it.
                const prev = s.matches[s.currentMatchIndex]
                let nextIndex = -1
                if (prev != null) {
                    const found = matches.findIndex(
                        m => m.sheetId === prev.sheetId && m.row === prev.row && m.col === prev.col
                    )
                    if (found >= 0) nextIndex = found
                }
                if (nextIndex < 0 && matches.length > 0) nextIndex = 0
                return { matches, currentMatchIndex: nextIndex, regexError }
            }),
        setCurrentMatchIndex: index => set({ currentMatchIndex: index }),
    }))
}
